import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface HistoryItem {
  session_id: string;
  session_title: string;
  last_ended_at: string;
  is_active?: boolean;
}

interface ApiResponse {
  sessions: HistoryItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export function useChatHistory() {
  const [sessions, setSessions] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<ApiResponse['pagination']>({
    total: 0,
    page: 1,
    limit: 20,
    total_pages: 0,
  });
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [searchQuery, setSearchQuery] = useState('');

  const fetchHistory = useCallback(
    async (page = 1, limit = 20, search = '', append = false) => {
      if (!token) {
        setError('Token missing');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        let url = `${API_BASE_URL}/chat/history/${token}/?page=${page}&limit=${limit}&include_active=true`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: ApiResponse = await response.json();

        if (append) {
          setSessions(prev => [...prev, ...data.sessions]);
        } else {
          setSessions(data.sessions || []);
        }

        setPagination(data.pagination);
      } catch (err: unknown) {
        console.error('📡 fetchHistory: Error occurred:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage || 'Failed to load chat history');
        if (!append) {
          setSessions([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  // Initial fetch on component mount or token change
  useEffect(() => {
    if (token) {
      fetchHistory(1, 20, '');
    }
  }, [token, fetchHistory]);

  // --- MODIFICATION START ---

  // REMOVED the problematic useEffect that was watching searchQuery.
  // The logic is now correctly placed inside the `search` function below.

  // The new `search` function is now responsible for triggering the data fetch.
  const search = useCallback(
    (query: string) => {
      // 1. Update the state for the UI (e.g., to show a chip with the search term).
      setSearchQuery(query);
      // 2. Directly trigger a new fetch from page 1 with the new query.
      //    If the query is '', this correctly fetches the full, unfiltered list,
      //    effectively resetting the state.
      fetchHistory(1, 20, query, false);
    },
    [fetchHistory]
  ); // Dependency on fetchHistory is crucial.

  // --- MODIFICATION END ---

  const groupedSessions = useCallback(() => {
    if (!sessions || sessions.length === 0) {
      return { recent: [], older: [] };
    }
    const recentChats = sessions.slice(0, 5);
    const olderChats = sessions.slice(5);
    return { recent: recentChats, older: olderChats };
  }, [sessions]);

  const loadMore = useCallback(() => {
    if (pagination.page < pagination.total_pages && !isLoading) {
      fetchHistory(pagination.page + 1, 20, searchQuery, true);
    }
  }, [pagination.page, pagination.total_pages, searchQuery, isLoading, fetchHistory]);

  const refreshHistory = useCallback(() => {
    if (!token) return;
    // This correctly re-fetches from page 1 using the current search query.
    // If searchQuery is empty, it refreshes the main list. If it has a term, it refreshes the search results.
    fetchHistory(1, 20, searchQuery);
  }, [searchQuery, token, fetchHistory]);

  return {
    history: groupedSessions(),
    sessions,
    isLoading,
    error,
    currentPage: pagination.page,
    totalPages: pagination.total_pages,
    totalItems: pagination.total,
    totalLoadedItems: sessions.length,
    hasMorePages: pagination.page < pagination.total_pages,
    refreshHistory,
    loadMore,
    search,
    searchQuery,
  };
}
